import { VoiceCallRecordingDto } from '@act/shared/data-transfer-objects';
import { Box, CircularProgress } from '@mui/material';
import { useApiCall } from 'redux/utils/api-hook';
import { callsRequests } from 'redux/requests/calls.request';
import { useState } from 'react';
import AccuracyQuestion from './AccuracyQuestion';
import NegativeQuestion from './NegativeQuestion';
import { INITIAL_POST_CALL_FEEDBACK_STATE } from './initialPostCallFeedbackState.const';
import { useToast } from '../../../app/ReusableToast';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';
import { TrackingProvider } from '../../../tracking';
import { PostCallFeedbackState, FeedbackType } from './types';

interface PostCallFeedbackProps {
  voiceCallRecording: VoiceCallRecordingDto;
  feedbackType: FeedbackType;
  onClose: () => void;
}

const PostCallFeedback = ({
  voiceCallRecording,
  feedbackType,
  onClose,
}: PostCallFeedbackProps) => {
  const callDataQualityFF = useFeatureFlag('call-data-quality');
  const toast = useToast();

  const [
    _updatedVoiceCallRecording,
    isUpdatingTranscriptionFeedback,
    updateVoiceCallRecording,
  ] = useApiCall(callsRequests.updateVoiceCallRecording);

  const [postCallFeedback, setPostCallFeedback] =
    useState<PostCallFeedbackState>(INITIAL_POST_CALL_FEEDBACK_STATE);

  async function submitFeedback(updatedFeedback?: PostCallFeedbackState) {
    const feedbackToSubmit = updatedFeedback || postCallFeedback;

    try {
      await updateVoiceCallRecording({
        params: { id: voiceCallRecording.id },
        body: {
          isAccurateTranscription: feedbackToSubmit.isAccurateTranscription,
          accuracyIssues:
            feedbackToSubmit.accuracyIssues.length > 0
              ? feedbackToSubmit.accuracyIssues
              : undefined,
          accuracyFeedback: feedbackToSubmit.accuracyFeedback,
          isAccurateSummary: feedbackToSubmit.isAccurateSummary,
          summaryAccuracyIssues:
            feedbackToSubmit.summaryAccuracyIssues.length > 0
              ? feedbackToSubmit.summaryAccuracyIssues
              : undefined,
          summaryAccuracyFeedback: feedbackToSubmit.summaryAccuracyFeedback,
        },
      });
      toast('Thank you for your feedback!', 'success');
      onClose();
    } catch (error) {
      toast('Failed to submit feedback. Please try again.', 'error');
    }
  }

  const isSummaryFeedback = feedbackType === 'summary';
  const isTranscriptFeedback = feedbackType === 'transcription';

  const shouldShowSummaryFeedback =
    isSummaryFeedback &&
    (postCallFeedback.isAccurateSummary === null ||
      postCallFeedback.isAccurateSummary === true);

  const shouldShowTranscriptFeedback =
    isTranscriptFeedback &&
    (postCallFeedback.isAccurateTranscription === null ||
      postCallFeedback.isAccurateTranscription === true);

  const shouldShowNegativeFeedback =
    (isSummaryFeedback && postCallFeedback.isAccurateSummary === false) ||
    (isTranscriptFeedback &&
      postCallFeedback.isAccurateTranscription === false);

  return (
    <TrackingProvider contextName="post-call-feedback">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          padding: '8px',
          margin: '8px 0 0',
          marginBottom: '0px',
          border: callDataQualityFF ? 'none' : '1px solid #E0E0E0',
          width: '100%',
          boxSizing: 'border-box',
          flexShrink: 0,
          maxHeight: '200px',
          overflow: 'auto',
        }}
      >
        {isUpdatingTranscriptionFeedback && <CircularProgress />}

        {(shouldShowSummaryFeedback || shouldShowTranscriptFeedback) && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccuracyQuestion
              feedbackType={feedbackType}
              postCallFeedback={postCallFeedback}
              isUpdatingPostCallFeedback={isUpdatingTranscriptionFeedback}
              setPostCallFeedback={setPostCallFeedback}
              onClose={onClose}
              submitFeedback={submitFeedback}
            />
          </Box>
        )}

        {shouldShowNegativeFeedback && (
          <NegativeQuestion
            feedbackType={feedbackType}
            postCallFeedback={postCallFeedback}
            isUpdatingPostCallFeedback={isUpdatingTranscriptionFeedback}
            setPostCallFeedback={setPostCallFeedback}
            onClose={onClose}
            submitFeedback={submitFeedback}
          />
        )}
      </Box>
    </TrackingProvider>
  );
};

export default PostCallFeedback;
